import React from 'react';
import { Select, Button } from 'antd';
import { CalendarOutlined, LinkOutlined } from '@ant-design/icons';
import { useIsOutletScoped } from '../../hooks/useIsOutletScoped';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useEventContext } from '../../contexts/EventContext';
import { useTenant } from '../../contexts/TenantContext';
import { EventsService, setTenantContext } from '../../api/client';
import { getLocalizedString } from '../../utils/locale';
import { useTranslation } from "../../hooks/useTranslation";
import { buildEventWebshopUrl } from '../../utils/webshopUrl';
import { formatLocalizedDate } from '../../utils/dateFormat';
import { resolveUILanguage } from '../../utils/eventSettingsHelpers';
// Remove CSS module import to use inline styles

const { Option } = Select;

export const EventSelector: React.FC = () => {
  const { selectedEvent, setSelectedEvent } = useEventContext();
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const tenant = useTenant();
  const { t, i18n } = useTranslation();
  const currentLang = resolveUILanguage(i18n.language);
  const outletScoped = useIsOutletScoped();

  // Fetch events for the current organization
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['events', orgSlug],
    queryFn: async () => {
      if (!orgSlug) return { results: [] };

      // Set tenant context for API calls
      if (tenant) {
        setTenantContext(tenant);
      }

      return await EventsService.getOrganizersEvents(
        orgSlug,
        0, // skip
        100, // take - Get first 100 events
        undefined, // search
        undefined, // isPublic
        undefined, // live
        undefined  // ordering
      );
    },
    enabled: !!orgSlug && !outletScoped, // vai theo outlet không có quyền đọc sự kiện (403) → không gọi
  });

  const handleEventChange = (eventSlug: string) => {
    if (!orgSlug) return;

    // Navigate to the event dashboard
    navigate(`/organizations/${orgSlug}/events/${eventSlug}`);
    setSelectedEvent(eventSlug);
  };

  const events = eventsData?.results || [];

  const handleViewWebshop = () => {
    if (!selectedEvent || !orgSlug) return;
    const webshopUrl = buildEventWebshopUrl(orgSlug, selectedEvent);
    window.open(webshopUrl, '_blank');
  };

  return (
    <div
      className="event-selector"
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.15)',
        background: 'rgba(255,255,255,0.04)',
      }}
    >
      <div
        style={{
          color: 'rgba(255,255,255,0.65)',
          fontSize: '12px',
          fontWeight: 500,
          display: 'block',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        <CalendarOutlined style={{ marginRight: '6px' }} />
        {t('common.context.eventContext')}
      </div>

      <Select
        value={selectedEvent || null}
        placeholder={t('common.context.selectAnEvent')}
        onChange={handleEventChange}
        loading={isLoading}
        className="event-selector-dropdown"
        style={{
          width: '100%',
          height: '60px',
        }}
        classNames={{
          popup: {
            root: 'event-selector-dropdown-menu'
          }
        }}
        size="small"
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) => {
          const optionText = option?.children?.toString().toLowerCase() || '';
          return optionText.includes(input.toLowerCase());
        }}
      >
        {events.map((event) => {
          const eventName = getLocalizedString(event.name, 'Untitled Event');
          const eventDate = event.date_from ? formatLocalizedDate(event.date_from, currentLang) : 'No date';

          return (
            <Option key={event.slug} value={event.slug}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span
                  style={{
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '200px'
                  }}
                  title={eventName}
                >
                  {eventName}
                </span>
                <span
                  style={{
                    color: 'rgba(255,255,255,0.45)',
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '200px',
                  }}
                  title={`Slug: ${event.slug} | ${eventDate}`}
                >
                  {event.slug} • {eventDate}
                </span>
              </div>
            </Option>
          );
        })}

        {events.length === 0 && !isLoading ? (
          <Option disabled value="">
            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>
              {t('common.messages.noData')}
            </span>
          </Option>
        ) : null}
      </Select>

      {/* View in Webshop Button */}
      {selectedEvent && (
        <Button
          type="text"
          size="small"
          icon={<LinkOutlined />}
          onClick={handleViewWebshop}
          style={{
            width: '100%',
            marginTop: '8px',
            color: 'rgba(255,255,255,0.75)',
            borderColor: 'rgba(255,255,255,0.2)',
            textAlign: 'center'
          }}
        >
          {t('common.actions.viewInWebshop') || 'View in Webshop'}
        </Button>
      )}
    </div>
  );
};